/**
 * Auth Context (Clerk Edition)
 * 
 * Provides authentication state and methods throughout the app.
 * Bridges Clerk identity with local persona management.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useUser, useAuth as useClerkAuth, useSession } from '@clerk/clerk-react';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const { isLoaded: isUserLoaded, user: clerkUser } = useUser();
    const { isLoaded: isAuthLoaded, userId, getToken, signOut: clerkSignOut } = useClerkAuth();
    const { session: clerkSession } = useSession();

    const [loading, setLoading] = useState(true);
    const [personas, setPersonas] = useState([]);
    const [activePersona, setActivePersona] = useState(null);

    // Sync loading state
    useEffect(() => {
        if (isUserLoaded && isAuthLoaded) {
            setLoading(false);
        }
    }, [isUserLoaded, isAuthLoaded]);

    const fetchPersonas = useCallback(async () => {
        if (!userId) return;

        try {
            const token = await getToken();
            const apiBase = import.meta.env.VITE_API_URL || '';
            const response = await fetch(`${apiBase}/api/profiles/me`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setPersonas(data.personas || []);

                // Set active persona to primary one, or first if none marked
                if (!activePersona && data.personas?.length > 0) {
                    const primary = data.personas.find(p => p.is_primary) || data.personas[0];
                    setActivePersona(primary);
                }
            }
        } catch (error) {
            console.error('Error fetching personas:', error);
        }
    }, [userId, getToken, activePersona]);

    // Fetch personas when user changes
    useEffect(() => {
        if (userId) {
            fetchPersonas();
        } else {
            setPersonas([]);
            setActivePersona(null);
        }
    }, [userId, fetchPersonas]);

    const signOut = async () => {
        await clerkSignOut();
        setPersonas([]);
        setActivePersona(null);
    };

    const value = {
        user: clerkUser ? {
            ...clerkUser,
            email: clerkUser.primaryEmailAddress?.emailAddress
        } : null,
        session: clerkSession,
        loading,
        personas,
        activePersona,
        setActivePersona,
        fetchPersonas,
        signOut,
        isAuthenticated: !!userId,
        getAuthHeaders: async () => {
            const token = await getToken();
            return token ? { 'Authorization': `Bearer ${token}` } : {};
        }
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}

export default AuthContext;
